/**
 * Gates section of `/agents/workflows` — RO-3-02c / #44399.
 *
 * Port of `excellent/app/agents/workflows/_gates-panel.tsx` +
 * `_gates-panel-client.tsx`. The Next server half called `listProjectGates(...)`
 * inline; here the list arrives through `useApi(gates.list)`. The mutating
 * "Run gates" button still drives `POST /api/gates/run` via the same contract
 * (`gates.run`) — both ops are already mounted on the Hono host
 * (`excellent/server/routes/tail.ts`).
 *
 * A gate is a shell command a project's OWNER declared — a DIFFERENT primitive
 * from a loop (optimizable prompt pipeline) or a compiled workflow (agentic
 * decision mined into rule-data). That is why this is its own section on the
 * fleet page, not a column of the studio.
 */

import * as React from "react";
import {
  Button,
  Card,
  IntentBadge,
  RowShell,
  EmptyState,
  SectionTitle,
  formatRelativeTime,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import {
  gates,
  type ProjectGatesWire,
} from "@excellent/ui/api/contracts/gates";

/** green if the last run passed every gate, red if any failed, neutral if never run / no config. */
function overallIntent(p: ProjectGatesWire): "success" | "danger" | "neutral" {
  if (!p.hasConfig || !p.lastRun) return "neutral";
  return p.lastRun.ok ? "success" : "danger";
}

function overallLabel(p: ProjectGatesWire): string {
  if (!p.hasConfig) return "not set up";
  if (!p.lastRun) return "never run";
  return p.lastRun.ok ? "passing" : "failing";
}

function ProjectCard({
  project,
  onRun,
  running,
}: {
  project: ProjectGatesWire;
  onRun: (appId: number) => void;
  running: boolean;
}) {
  const resultById = new Map((project.lastRun?.results ?? []).map((r) => [r.id, r]));

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="type-body font-medium text-foreground">{project.name}</span>
          <span className="font-mono type-subtext text-muted-foreground">{project.root}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <IntentBadge intent={overallIntent(project)}>{overallLabel(project)}</IntentBadge>
          <Button size="sm" onClick={() => onRun(project.appId)} disabled={running}>
            {running ? "Running…" : "Run gates"}
          </Button>
        </div>
      </div>

      {project.gates.length === 0 ? (
        <p className="type-subtext text-muted-foreground">
          No check-*/test-* scripts detected yet — running gates here will create a starter
          .excellent/gates.json to edit.
        </p>
      ) : (
        <div className="flex flex-col">
          {project.gates.map((g) => {
            const r = resultById.get(g.id);
            return (
              <RowShell key={g.id} gutter={false} className="justify-between">
                <span className="type-subtext text-foreground">{g.label}</span>
                <span className="flex items-center gap-2 type-subtext text-muted-foreground">
                  {!g.enabled ? (
                    <IntentBadge intent="neutral">disabled</IntentBadge>
                  ) : r ? (
                    <>
                      <IntentBadge intent={r.ok ? "success" : "danger"}>
                        {r.ok ? "pass" : "fail"}
                      </IntentBadge>
                      <span>{(r.ms / 1000).toFixed(1)}s</span>
                    </>
                  ) : (
                    <IntentBadge intent="neutral">never run</IntentBadge>
                  )}
                </span>
              </RowShell>
            );
          })}
        </div>
      )}

      {project.lastRun && (
        <p className="type-subtext text-muted-foreground">
          Last run {formatRelativeTime(project.lastRun.ranAt)} — {project.lastRun.passed}/
          {project.lastRun.total} passed.
        </p>
      )}
    </Card>
  );
}

function GatesPanelBody({ initialProjects }: { initialProjects: ProjectGatesWire[] }) {
  const [projects, setProjects] = React.useState(initialProjects);
  const [runningId, setRunningId] = React.useState<number | null>(null);
  const [, startTransition] = React.useTransition();

  // Keep local state in sync when SWR revalidates the list.
  React.useEffect(() => {
    setProjects(initialProjects);
  }, [initialProjects]);

  function handleRun(appId: number) {
    setRunningId(appId);
    startTransition(async () => {
      try {
        const { project } = await apiCall(gates.run, { body: { appId } });
        setProjects((prev) => prev.map((p) => (p.appId === appId ? project : p)));
      } finally {
        setRunningId(null);
      }
    });
  }

  if (projects.length === 0) {
    return (
      <EmptyState
        title="No local projects registered yet"
        description="Gates run against a registered local app's root — register one to get find-or-create verify checks for it."
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 @2xl/page:grid-cols-2">
      {projects.map((p) => (
        <ProjectCard key={p.appId} project={p} onRun={handleRun} running={runningId === p.appId} />
      ))}
    </div>
  );
}

export function GatesPanel() {
  const { data, error, isLoading } = useApi(gates.list, {});

  return (
    <section className="flex flex-col gap-4" data-section-gates="agents">
      <SectionTitle
        variant="plain"
        size="lg"
        title="Gates"
        subtitle="The ship/verify checks for your own local projects — found or created from what each project already has, run on demand."
      />
      {error ? (
        <p className="type-body text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </p>
      ) : null}
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : (
        <GatesPanelBody initialProjects={data?.projects ?? []} />
      )}
    </section>
  );
}
